import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axiosInstance from "../../helpers/axiosInstance";
import Dropzone from "react-dropzone";

const EcommerceSubCategories = () => {
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    image: null,
  });
  const [previewImage, setPreviewImage] = useState(null);

  const [breadcrumbItems] = useState([
    { title: "Ecommerce", link: "#" },
    { title: "Sub Categories", link: "#" },
  ]);

  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get("api/v1/categories");
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchSubCategories = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("api/v1/subcategories");
      setSubCategories(response.data.subcategories);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    setModal(!modal);
    if (!modal) {
      setFormData({ name: "", category: "", image: null });
      setPreviewImage(null);
      setEditMode(false);
      setSelectedSubCategory(null);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const uploadImageToOm = async (file) => {
    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append("image", file);

      const response = await axiosInstance.post(
        "api/v1/upload-image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data.images;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    try {
      setPreviewImage(URL.createObjectURL(file));
      const imageUrl = await uploadImageToOm(file);
      setFormData((prev) => ({
        ...prev,
        image: imageUrl,
      }));
    } catch (error) {
      alert("Failed to upload image. Please try again.");
      setPreviewImage(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category) {
      alert("Please select a category");
      return;
    }
    setLoading(true);
    try {
      if (editMode) {
        await axiosInstance.put(
          `api/v1/admin/subcategory/${selectedSubCategory._id}`,
          formData
        );
      } else {
        await axiosInstance.post("api/v1/admin/subcategory", formData);
      }
      fetchSubCategories();
      toggle();
    } catch (error) {
      console.error("Error saving subcategory:", error);
      alert(error.response?.data?.message || "Error saving subcategory");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (subCategory) => {
    setSelectedSubCategory(subCategory);
    setFormData({
      name: subCategory.name,
      category: subCategory.category._id,
      image: subCategory.image,
    });
    setPreviewImage(subCategory.image);
    setEditMode(true);
    setModal(true);
  };

  const handleDelete = async (subCategoryId) => {
    if (window.confirm("Are you sure you want to delete this subcategory?")) {
      try {
        setLoading(true);
        await axiosInstance.delete(`api/v1/admin/subcategory/${subCategoryId}`);
        fetchSubCategories();
      } catch (error) {
        console.error("Error deleting subcategory:", error);
        alert(error.response?.data?.message || "Error deleting subcategory");
      } finally {
        setLoading(false);
      }
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat._id === categoryId);
    return category ? category.name : "";
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Sub Categories" breadcrumbItems={breadcrumbItems} />

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <CardTitle className="h4">Sub Category Management</CardTitle>
                  <Button color="primary" onClick={toggle}>
                    Add New Sub Category
                  </Button>
                </div>

                {loading ? (
                  <div className="text-center">
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-centered table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Image</th>
                          <th>Name</th>
                          <th>Parent Category</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subCategories?.map((subCategory) => (
                          <tr key={subCategory._id}>
                            <td>
                              <img
                                src={subCategory.image.startsWith("https") ? subCategory.image : `${process.env.REACT_APP_BASEURL}${subCategory.image}`}
                                alt={subCategory.name}
                                className="rounded"
                                height="48"
                              />
                            </td>
                            <td>{subCategory.name}</td>
                            <td>{getCategoryName(subCategory.category)}</td>
                            <td>
                              <Button
                                color="info"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEdit(subCategory)}
                              >
                                Edit
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => handleDelete(subCategory._id)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={modal} toggle={toggle} size="lg">
          <ModalHeader toggle={toggle}>
            {editMode ? "Edit Sub Category" : "Add New Sub Category"}
          </ModalHeader>
          <ModalBody>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label for="category">Parent Category *</Label>
                <Input
                  type="select"
                  name="category"
                  id="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Category</option>
                  {categories?.map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>

              <FormGroup>
                <Label for="name">Sub Category Name *</Label>
                <Input
                  type="text"
                  name="name"
                  id="name"
                  placeholder="Enter sub category name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Sub Category Image *</Label>
                <div className="dropzone">
                  <Dropzone
                    onDrop={handleImageDrop}
                    accept={{
                      "image/*": [".png", ".jpg", ".jpeg"],
                    }}
                    multiple={false}
                    disabled={uploadingImage}
                  >
                    {({ getRootProps, getInputProps }) => (
                      <div
                        className="dz-message needsclick"
                        {...getRootProps()}
                      >
                        <input {...getInputProps()} />
                        <div className="mb-3">
                          <i className="display-4 text-muted ri-upload-cloud-2-line" />
                        </div>
                        <h4>
                          {uploadingImage
                            ? "Uploading image..."
                            : "Drop an image here or click to upload."}
                        </h4>
                      </div>
                    )}
                  </Dropzone>
                </div>
                {previewImage && (
                  <div className="mt-3">
                    <img
                      src={previewImage}
                      alt="Preview"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        objectFit: "contain",
                      }}
                      className="img-fluid"
                    />
                  </div>
                )}
              </FormGroup>

              <div className="text-end mt-4">
                <Button
                  type="button"
                  color="light"
                  onClick={toggle}
                  className="me-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={
                    loading ||
                    uploadingImage ||
                    !formData.image ||
                    !formData.category
                  }
                >
                  {loading ? <Spinner size="sm" /> : "Save Sub Category"}
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default EcommerceSubCategories;
